import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'

const { recordModel = '/static/models/record.glb' } = window.psdigital ?? {}
let pending = null
const progress = { fraction: 0, listeners: new Set() } // the glb's download, 0..1 (it's most of the first load)

/** @param {(fraction: number) => void} listener */
export function onModelProgress(listener) {
	progress.listeners.add(listener)
	listener(progress.fraction)
}

/**
 * The record model, loaded once and shared by every Record (see scripts/compress-record.sh):
 * meshopt geometry with 2k near-lossless WebP textures. Two maps this size don't need GPU
 * compression, and plain WebP skips the basis transcoder (0.5MB + a WASM decode) entirely.
 *
 * Two meshes, "record" and "label", flat in XY with +Z facing out, sharing one material
 * whose normal + ORM maps are returned for VinylMaterial. There's no diffuse: the colour
 * comes from the procedural pattern. The geometry is quantised, so each mesh's own
 * transform matters: `radius` is the record's, with that applied, in the model's units.
 *
 * @returns {Promise<{ record: THREE.Mesh, label: THREE.Mesh, maps: object, radius: number }>}
 */
export function loadRecordModel() {
	if (pending) return pending

	const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder)
	const onProgress = event => {
		if (!event.lengthComputable) return
		progress.fraction = event.loaded / event.total
		progress.listeners.forEach(listener => listener(progress.fraction))
	}
	pending = loader.loadAsync(recordModel, onProgress).then(gltf => {
		const record = gltf.scene.getObjectByName('record')
		const label = gltf.scene.getObjectByName('label')
		const { normalMap, normalScale, aoMap, aoMapIntensity, roughnessMap, metalnessMap } = record.material
		record.geometry.computeBoundingSphere()

		return {
			record,
			label,
			maps: { normalMap, normalScale, aoMap, aoMapIntensity, roughnessMap, metalnessMap },
			radius: record.geometry.boundingSphere.radius * record.scale.x,
		}
	})
	return pending
}
